import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare, X, Sparkles, Send, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail, websiteChannelHeaders } from "../lib/api";

import { useTranslation } from 'react-i18next';
const STORAGE_KEY = "maxemployee.sales_chat_session_id";
const HIDDEN_PREFIXES = ["/support", "/dashboard", "/checkout", "/admin", "/course", "/login", "/register", "/signup", "/onboarding"];

function isHomePage(pathname) {
  return pathname === "/";
}

function makeSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, "");
  return `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function formatTime(value) {
  try {
    return new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function FloatingSalesChat() {
  const { t } = useTranslation();
  const location = useLocation();
  const [sessionId, setSessionId] = useState("");
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const endRef = useRef(null);

  const hidden = useMemo(() => isHomePage(location.pathname) || HIDDEN_PREFIXES.some((prefix) => location.pathname.startsWith(prefix)), [location.pathname]);

  useEffect(() => {
    if (hidden) {
      setOpen(false);
      return;
    }
    const existing = window.localStorage.getItem(STORAGE_KEY);
    const next = existing || makeSessionId();
    if (!existing) window.localStorage.setItem(STORAGE_KEY, next);
    setSessionId(next);
  }, [hidden]);

  const fetchThread = useCallback(async (sid) => {
    if (!sid || hidden) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/sales/chat", { params: { session_id: sid, product_id: "max-os-cloud" }, headers: websiteChannelHeaders("website-public") });
      setThread(data.thread || null);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail || e?.message));
    } finally {
      setLoading(false);
    }
  }, [hidden]);

  useEffect(() => {
    if (sessionId) fetchThread(sessionId);
  }, [sessionId, fetchThread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading, sending]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending || !sessionId || hidden) return;
    setSending(true);
    setError("");
    try {
      const { data } = await api.post("/sales/chat", { session_id: sessionId, product_id: "max-os-cloud", message: text, channel_origin: "website-public" }, { headers: websiteChannelHeaders("website-public") });
      const newMessages = Array.isArray(data.messages) ? data.messages : [];
      setThread(data.thread || thread);
      setMessages((prev) => [...prev, ...newMessages]);
      setInput("");
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail || e?.message));
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('floating_sales_chat.open_sales_chat')}
          className="fixed bottom-6 right-6 z-[70] inline-flex items-center gap-3 rounded-full border border-amber-500/30 bg-zinc-950/95 px-4 py-3 text-sm text-white shadow-2xl shadow-black/40 transition-transform hover:scale-[1.02] hover:border-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500 text-black" aria-hidden="true">
            <MessageSquare className="h-4 w-4" />
          </span>
          <span className="flex flex-col items-start text-left">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">{t('floating_sales_chat.max_sales')}</span>
            <span className="text-xs text-zinc-400">{t('floating_sales_chat.talk_about_products_pricing_and_fit')}</span>
          </span>
        </button>
      )}

      {open && (
        <div
          id="sales-chat-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t('floating_sales_chat.sales_chat')}
          className="fixed bottom-4 right-4 z-[80] w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-3xl border border-zinc-800 bg-[#09090B] shadow-2xl shadow-black/60"
        >
          <div className="flex items-center justify-between gap-4 border-b border-zinc-800 px-4 py-3 bg-zinc-950/95">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500" aria-hidden="true">// MAX SALES</div>
              <div className="mt-1 flex items-center gap-2 text-sm text-zinc-200">
                <Sparkles className="h-4 w-4 text-amber-500" aria-hidden="true" />
                {t('floating_sales_chat.sales_chat_online')}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full border border-zinc-800 p-2 text-zinc-400 transition-colors hover:border-zinc-600 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              aria-label={t('floating_sales_chat.close_sales_chat')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[78vh] overflow-auto">
            <div className="border-b border-zinc-800 px-4 py-3 text-sm text-zinc-400">
              {t('floating_sales_chat.ask_about_max_os_cloud')}
            </div>

            <div className="px-4 py-4 space-y-3 min-h-[280px]" role="log" aria-live="polite" aria-relevant="additions">
              {loading ? (
                <div className="flex items-center justify-center py-12 text-zinc-400 gap-2 font-mono text-[10px] uppercase tracking-[0.2em]">
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('floating_sales_chat.loading_sales_chat')}
                </div>
              ) : (
                messages.map((message) => {
                  const isUser = message.sender === "user";
                  return (
                    <div key={message.message_id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed border ${isUser ? "bg-amber-500 text-black border-amber-400/40" : "bg-zinc-900 text-zinc-100 border-zinc-800"}`}>
                        <div className={`mb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] ${isUser ? "text-black/70" : "text-amber-300"}`}>
                          <span>{isUser ? t('floating_sales_chat.you') : (message.author_name || t('floating_sales_chat.max_sales'))}</span>
                          <span>{formatTime(message.created_at)}</span>
                        </div>
                        <div className="whitespace-pre-wrap">{message.content}</div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={endRef} />
            </div>

            <div className="border-t border-zinc-800 p-4">
              <div className="flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={t('floating_sales_chat.ask_about_pricing_product_fit_or_next_steps')}
                  aria-label={t('floating_sales_chat.chat_message_input')}
                  className="flex-1 rounded-2xl border border-zinc-800 bg-zinc-950 px-4 py-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-amber-500/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                />
                <button
                  type="button"
                  onClick={sendMessage}
                  disabled={sending || !input.trim()}
                  aria-label={t('floating_sales_chat.send_message')}
                  className="inline-flex items-center justify-center rounded-2xl bg-amber-500 px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
              {error && <div className="mt-3 text-xs text-red-300">{error}</div>}
              <div className="mt-3 text-[11px] text-zinc-500">
                {t('floating_sales_chat.already_customer_directs_to_support')}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
